import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { env } from '@/lib/env';

const API_URL = env.VITE_API_URL;

export default function DeleteSellerAccountPage() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<{ code?: string; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/public/request/delete/seller`, {
        email,
        reason: reason || undefined,
      });

      if (response.data?.data?.success) {
        setSuccess(true);
      }
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      if (errorData?.code) {
        setError({ code: errorData.code, message: errorData.message });
      } else {
        setError({ message: 'Une erreur est survenue. Veuillez reessayer.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Email envoye</h1>
          <p className="text-gray-600 mb-4">
            Si un compte existe avec cette adresse email, vous recevrez un email de confirmation
            dans les prochaines minutes. Veuillez verifier votre boite de reception et vos spams.
          </p>
          <p className="text-sm text-gray-500">
            Le lien de confirmation est valable pendant 4 heures.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-xl w-full bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 mb-2">
            <img src="/volovolo.png" alt="Volovolo" className="h-8 w-auto" />
            <span className="font-bold text-xl">Pro</span>
          </Link>
          <h1 className="text-xl text-gray-700">Demande de suppression de compte</h1>
        </div>

        {/* Warning Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h3 className="font-semibold text-amber-800">Cette action est irreversible</h3>
              <p className="text-sm text-amber-700">
                Une fois confirmee, toutes vos donnees seront supprimees definitivement.
              </p>
            </div>
          </div>
        </div>

        {/* Consequences List */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Consequences de la suppression :</h3>
          <ul className="space-y-2">
            {[
              'Votre boutique sera definitivement desactivee',
              'Tous vos produits seront retires de la vente',
              'Vos informations personnelles seront supprimees',
              'Votre historique de ventes sera anonymise',
              'Vous perdrez l\'acces a vos statistiques',
            ].map((item, index) => (
              <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error.message}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Adresse email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="votre@email.com"
            />
            <p className="text-xs text-gray-500 mt-1">Entrez l'email associe a votre compte vendeur</p>
          </div>

          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
              Raison de la suppression (optionnel)
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="Aidez-nous a ameliorer nos services..."
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !email}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Envoi en cours...
              </>
            ) : (
              'Demander la suppression'
            )}
          </button>
        </form>

        <p className="text-sm text-gray-500 text-center mt-4">
          Vous recevrez un email de confirmation. La suppression ne sera effective
          qu'apres avoir clique sur le lien de confirmation.
        </p>
      </div>
    </div>
  );
}
